import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { assetsApi, catalogApi } from '@/api/endpoints';
import { queryKeys } from '@/api/queryKeys';
import { usePermission } from '@/auth/AuthProvider';
import { PERMISSIONS } from '@/auth/permissions';
import type {
  AssetLabel,
  ContractKind,
  ContractStanding,
  IntervalUnit,
  ServiceContract,
  WorkOrderKind,
  WorkOrderStatus,
} from '@/api/types';
import { StatusBadge, type StatusTone } from '@/design-system/DataDisplay';
import { SearchInput } from '@/design-system/Inputs';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/cn';
import { formatMoney } from '@/lib/format';

/**
 * The vocabulary shared by the maintenance and the warranty / AMC screens, so a work order or a
 * contract reads the same in its own list, on its detail page and on the asset it concerns.
 */

// ------------------------------------------------------------------ labels and options

export const WORK_ORDER_STATUS: Record<WorkOrderStatus, { label: string; tone: StatusTone }> = {
  OPEN: { label: 'Open', tone: 'info' },
  IN_PROGRESS: { label: 'In progress', tone: 'brand' },
  ON_HOLD: { label: 'On hold', tone: 'warning' },
  COMPLETED: { label: 'Completed', tone: 'success' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export const WORK_ORDER_KIND: Record<WorkOrderKind, string> = {
  PREVENTIVE: 'Preventive',
  BREAKDOWN: 'Breakdown',
};

export const CONTRACT_KIND: Record<ContractKind, string> = {
  WARRANTY: 'Warranty',
  EXTENDED_WARRANTY: 'Extended warranty',
  AMC: 'AMC',
};

export const CONTRACT_STANDING: Record<ContractStanding, { label: string; tone: StatusTone }> = {
  UPCOMING: { label: 'Upcoming', tone: 'info' },
  IN_FORCE: { label: 'In force', tone: 'success' },
  RENEWAL_DUE: { label: 'Renewal due', tone: 'warning' },
  EXPIRED: { label: 'Expired', tone: 'danger' },
  RENEWED: { label: 'Renewed', tone: 'neutral' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export const INTERVAL_UNIT: Record<IntervalUnit, { one: string; many: string }> = {
  DAYS: { one: 'day', many: 'days' },
  WEEKS: { one: 'week', many: 'weeks' },
  MONTHS: { one: 'month', many: 'months' },
  YEARS: { one: 'year', many: 'years' },
};

export const PRIORITY_OPTIONS = [
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
];

/** `{ value, label }` pairs for a Select, from one of the label maps above. */
export function optionsOf<K extends string>(labels: Record<K, string | { label: string }>) {
  return (Object.keys(labels) as K[]).map((value) => {
    const entry = labels[value];
    return { value, label: typeof entry === 'string' ? entry : entry.label };
  });
}

// ------------------------------------------------------------------ badges and formatting

export function WorkOrderStatusBadge({ status, overdue }: { status?: WorkOrderStatus; overdue?: boolean }) {
  if (!status) return null;
  if (overdue) return <StatusBadge tone="danger">Overdue</StatusBadge>;
  const style = WORK_ORDER_STATUS[status];
  return <StatusBadge tone={style.tone}>{style.label}</StatusBadge>;
}

export function ContractStandingBadge({ standing }: { standing?: ContractStanding }) {
  if (!standing) return null;
  const style = CONTRACT_STANDING[standing];
  return <StatusBadge tone={style.tone}>{style.label}</StatusBadge>;
}

/** "12d left", "Ended 3 days ago" - the end date as the distance a reader cares about. */
export function daysLeftLabel(contract: ServiceContract) {
  const days = contract.daysRemaining ?? 0;
  if (contract.standing === 'RENEWED' || contract.standing === 'CANCELLED') return '';
  if (days < 0) return `Ended ${Math.abs(days)}d ago`;
  if (days === 0) return 'Ends today';
  return `${days}d left`;
}

/** "Every 3 months" */
export function intervalLabel(value?: number, unit?: IntervalUnit) {
  if (!value || !unit) return '—';
  const words = INTERVAL_UNIT[unit];
  return value === 1 ? `Every ${words.one}` : `Every ${value} ${words.many}`;
}

/** "2d 4h", "35m" - downtime at a glance. */
export function formatDowntime(minutes?: number) {
  if (!minutes) return '—';
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  return `${mins}m`;
}

/** "₹24,000 + $300" - a per-currency total, since amounts in different currencies never add. */
export function formatMoneyByCurrency(totals?: Record<string, number>, empty = '—') {
  const entries = Object.entries(totals ?? {});
  if (entries.length === 0) return empty;
  return entries.map(([currency, amount]) => formatMoney(amount, currency)).join(' + ');
}

/** How a person recognises an asset: its tag, else its serial number. */
export function assetName(asset?: AssetLabel | { assetTag?: string | null; serialNumber?: string | null }) {
  return asset?.assetTag || asset?.serialNumber || 'Untagged asset';
}

/** Today as `yyyy-mm-dd`, plus an offset, for date inputs. */
export function isoDate(offsetDays = 0) {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
}

// ------------------------------------------------------------------ lookups

/** Vendors by id, for naming them in tables. The catalogue is small and changes rarely. */
export function useVendors() {
  const canRead = usePermission(PERMISSIONS.ASSET_READ);
  const vendors = useQuery({
    queryKey: queryKeys.catalog.vendors(),
    queryFn: catalogApi.vendors,
    staleTime: 10 * 60_000,
    enabled: canRead,
  });
  const nameOf = useMemo(() => {
    const byId = new Map((canRead ? vendors.data ?? [] : []).map((vendor) => [vendor.id, vendor.name]));
    return (id?: string | null) => (id ? byId.get(id) : undefined);
  }, [vendors.data, canRead]);
  const options = useMemo(
    () => [
      { value: '', label: 'No vendor' },
      ...(canRead ? vendors.data ?? [] : [])
        .filter((vendor) => vendor.active)
        .map((vendor) => ({ value: vendor.id, label: vendor.name })),
    ],
    [vendors.data, canRead],
  );
  const optionsFor = (id?: string) => !canRead && id
    ? [...options, { value: id, label: 'Recorded vendor' }] : options;
  return { nameOf, options, optionsFor, canRead };
}

/**
 * Names for assets known only by id. The first fifty are fetched; any further show as an ellipsis.
 */
export function useAssetNames(ids: string[]) {
  const wanted = ids.slice(0, 50);
  const fetched = useQueries({
    queries: wanted.map((id) => ({
      queryKey: queryKeys.assets.detail(id),
      queryFn: () => assetsApi.get(id),
      staleTime: 5 * 60_000,
    })),
  });
  return (id: string) => {
    const asset = fetched[wanted.indexOf(id)]?.data;
    return asset ? assetName(asset) : '…';
  };
}

// ------------------------------------------------------------------ asset picker

/**
 * Choose one asset, or several, by searching the register.
 *
 * Selected assets stay listed above the results so a long selection can be reviewed and trimmed
 * without scrolling the search.
 */
export function AssetPicker({
  value,
  onChange,
  multiple = false,
  label = multiple ? 'Assets' : 'Asset',
  required = false,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  multiple?: boolean;
  label?: string;
  required?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [known, setKnown] = useState<Record<string, string>>({});
  const debounced = useDebouncedValue(search, 250);
  const params = { search: debounced || undefined, size: 20 };
  const results = useQuery({
    queryKey: queryKeys.assets.list(params),
    queryFn: () => assetsApi.search(params),
    placeholderData: (previous) => previous,
  });

  // Assets selected before this picker opened - an existing contract's coverage - are named by
  // fetching them.
  const fetchedName = useAssetNames(value.filter((id) => !known[id]));
  const nameOf = (id: string) => known[id] ?? fetchedName(id);

  const toggle = (id: string, name: string) => {
    setKnown((current) => ({ ...current, [id]: name }));
    if (!multiple) {
      onChange(value.includes(id) ? [] : [id]);
      return;
    }
    onChange(value.includes(id) ? value.filter((entry) => entry !== id) : [...value, id]);
  };

  return (
    <div className="space-y-2">
      <span className="block text-small font-medium text-text-primary">
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
        {multiple && value.length > 0 && (
          <span className="ml-2 text-caption font-normal text-text-muted">{value.length} selected</span>
        )}
      </span>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => toggle(id, nameOf(id))}
              className="rounded-md bg-brand-subtle px-2 py-0.5 text-caption font-medium text-brand-text hover:bg-danger-subtle hover:text-danger-text"
              title="Remove"
            >
              {nameOf(id)} ×
            </button>
          ))}
        </div>
      )}

      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder="Search by tag, serial number or model…"
        aria-label="Search assets"
        className="w-full sm:w-full"
      />
      <div className="max-h-52 overflow-y-auto rounded-lg border border-border-subtle">
        {results.isLoading ? (
          <p className="px-3 py-4 text-small text-text-muted">Searching…</p>
        ) : !results.data?.content.length ? (
          <p className="px-3 py-4 text-small text-text-muted">No assets matched.</p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {results.data.content.map((asset) => {
              const id = asset.id ?? '';
              const name = assetName(asset);
              const selected = value.includes(id);
              return (
                <li key={id}>
                  <button
                    type="button"
                    aria-pressed={selected}
                    onClick={() => toggle(id, name)}
                    className={cn(
                      'flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-surface-hover',
                      selected && 'bg-brand-subtle',
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-small font-medium text-text-primary">{name}</span>
                      <span className="block truncate text-caption text-text-muted">
                        {[asset.serialNumber, asset.status?.replace(/_/g, ' ').toLowerCase()]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                    {selected && <Check aria-hidden className="size-4 shrink-0 text-brand-text" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
